import React from "react";
import {
  Box,
  Pagination,
  Slider,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  AutoAwesome as AutoAwesomeIcon,
  MenuOpen as ObjectInspectorIcon,
  Menu as BlockPanelIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Visibility as VisibilityIcon,
  Badge as BadgeIcon,
  PostAdd as PostAddIcon,
} from "@mui/icons-material";
import {
  LuMousePointer2,
  LuHand,
  LuSquareCheck,
  LuTextCursor,
  LuType,
  LuMinus,
  LuSquare,
  LuUndo,
  LuRedo,
  LuTextSelect,
  LuLayoutGrid,
  LuTable2,
} from "react-icons/lu";

const PDFEditorControlsBar = ({
  activeTool,
  onToolSelect,
  scale,
  handleScaleChange,
  highlightFields,
  setHighlightFields,
  textOverlayDisplayMode,
  setTextOverlayDisplayMode,
  numPages,
  pageNumber,
  handlePageChange,
  objectInspectorOpen,
  onToggleObjectInspector,
  blockPanelOpen,
  onToggleBlockPanel,
  undoRedoStats,
  textSelectionMode,
  setTextSelectionMode,
  loading,
  addFieldOpen,
  onToggleAddField,
  onAutoTagFields,
  handleMakeRowGroup,
  selectedFieldsCount = 0,
  showPaisTableButton = false,
  paisTableSelectActive = false,
  onTogglePaisTableSelect,
  characterConnectionControl = null,
  textConnectionControl = null,
}) => {
  const toolButtons = [
    { key: "edit", label: "Edit Tool", icon: <LuMousePointer2 size={18} /> },
    { key: "hand", label: "Hand Tool", icon: <LuHand size={18} /> },
    {
      key: "checkbox",
      label: "Check Box Tool",
      icon: <LuSquareCheck size={18} />,
    },
    {
      key: "textField",
      label: "Text Field Tool",
      icon: <LuTextCursor size={18} />,
    },
    {
      key: "insertText",
      label: "Insert Text Tool",
      icon: <LuType size={18} />,
    },
    { key: "rectangle", label: "Rectangle Tool", icon: <LuSquare size={18} /> },
    { key: "line", label: "Line Tool", icon: <LuMinus size={18} /> },
  ];

  const undoRedoButtons = [
    {
      key: "undo",
      label: "Undo",
      icon: <LuUndo size={18} />,
      handler: undoRedoStats?.undo,
      isDisabled: !undoRedoStats?.canUndo,
    },
    {
      key: "redo",
      label: "Redo",
      icon: <LuRedo size={18} />,
      handler: undoRedoStats?.redo,
      isDisabled: !undoRedoStats?.canRedo,
    },
  ];

  const nextTextOverlayMode = () => {
    const order = ["label", "name", "none"];
    const currentIndex = order.indexOf(textOverlayDisplayMode);
    return order[(currentIndex + 1) % order.length];
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        p: 2,
        borderBottom: "1px solid #e0e0e0",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip title="Block Assignment Panel">
          <IconButton
            size="small"
            onClick={onToggleBlockPanel}
            sx={{
              border: "1px solid #d0d7de",
              borderRadius: 1.5,
              backgroundColor: blockPanelOpen
                ? "rgba(25,118,210,0.12)"
                : "transparent",
            }}
          >
            <BlockPanelIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          flexWrap: "wrap",
        }}
      >
        {toolButtons.map((tool) => {
          const isActive = activeTool === tool.key;
          return (
            <Tooltip key={tool.key} title={tool.label}>
              <IconButton
                size="small"
                onClick={() => onToolSelect?.(tool.key)}
                disabled={loading}
                sx={{
                  border: "1px solid #d0d7de",
                  borderRadius: 1.5,
                  backgroundColor: isActive ? "primary.main" : "transparent",
                  color: isActive ? "white" : "text.primary",
                  "&:hover": {
                    backgroundColor: isActive
                      ? "primary.dark"
                      : "rgba(25,118,210,0.08)",
                  },
                }}
              >
                {tool.icon}
              </IconButton>
            </Tooltip>
          );
        })}

        <Typography variant="body2">Zoom:</Typography>
        <Tooltip title="Zoom Out">
          <IconButton
            size="small"
            disabled={loading}
            onClick={() =>
              handleScaleChange?.(null, Math.max(scale - 0.1, 0.5))
            }
          >
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Slider
          value={scale}
          onChange={handleScaleChange}
          min={0.5}
          max={2.0}
          step={0.1}
          sx={{ width: 100 }}
          disabled={loading}
        />
        <Tooltip title="Zoom In">
          <IconButton
            size="small"
            disabled={loading}
            onClick={() =>
              handleScaleChange?.(null, Math.min(scale + 0.1, 2.0))
            }
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="body2">{Math.round(scale * 100)}%</Typography>

        <Tooltip title="Show highlight">
          <IconButton
            size="small"
            disabled={loading}
            onClick={() => setHighlightFields(!highlightFields)}
            sx={{
              border: "1px solid #d0d7de",
              borderRadius: 1.5,
              color: highlightFields ? "warning.main" : "text.primary",
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip
          title={`Text overlay: ${textOverlayDisplayMode === "none" ? "None" : textOverlayDisplayMode === "label" ? "Label" : "Name"}`}
        >
          <IconButton
            size="small"
            disabled={loading}
            onClick={() => setTextOverlayDisplayMode?.(nextTextOverlayMode())}
            sx={{
              border: "1px solid #d0d7de",
              borderRadius: 1.5,
              color:
                textOverlayDisplayMode === "none"
                  ? "text.secondary"
                  : "primary.main",
            }}
          >
            <BadgeIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Text Selection">
          <IconButton
            size="small"
            disabled={loading}
            onClick={() => setTextSelectionMode(!textSelectionMode)}
            sx={{
              border: "1px solid #d0d7de",
              borderRadius: 1.5,
              backgroundColor: textSelectionMode
                ? "primary.main"
                : "transparent",
              color: textSelectionMode ? "white" : "text.primary",
              "&:hover": {
                backgroundColor: textSelectionMode
                  ? "primary.dark"
                  : "rgba(25,118,210,0.08)",
              },
            }}
          >
            <LuTextSelect size={18} />
          </IconButton>
        </Tooltip>

        <Tooltip
          title={
            selectedFieldsCount < 2
              ? "Select at least 2 fields to group"
              : "Group into one row"
          }
        >
          <span>
            <IconButton
              size="small"
              disabled={loading || selectedFieldsCount < 2}
              onClick={handleMakeRowGroup}
              sx={{
                border: "1px solid #d0d7de",
                borderRadius: 1.5,
                backgroundColor: "transparent",
                color:
                  selectedFieldsCount < 2 ? "text.disabled" : "text.primary",
                "&:hover": {
                  backgroundColor: "rgba(25,118,210,0.08)",
                },
              }}
            >
              <LuLayoutGrid size={18} />
            </IconButton>
          </span>
        </Tooltip>

        {showPaisTableButton && (
          <Tooltip title="Tag Table Fields">
            <IconButton
              size="small"
              disabled={loading}
              onClick={onTogglePaisTableSelect}
              sx={{
                border: "1px solid #d0d7de",
                borderRadius: 1.5,
                backgroundColor: paisTableSelectActive
                  ? "primary.main"
                  : "transparent",
                color: paisTableSelectActive ? "white" : "text.primary",
                "&:hover": {
                  backgroundColor: paisTableSelectActive
                    ? "primary.dark"
                    : "rgba(25,118,210,0.08)",
                },
              }}
            >
              <LuTable2 size={18} />
            </IconButton>
          </Tooltip>
        )}

        {characterConnectionControl}
        {textConnectionControl}

        <Tooltip title="Auto Tag Fields">
          <span>
            <IconButton
              size="small"
              disabled={loading}
              onClick={onAutoTagFields}
              sx={{
                border: "1px solid #d0d7de",
                borderRadius: 1.5,
                backgroundColor: "transparent",
                color: "text.primary",
                "&:hover": {
                  backgroundColor: "rgba(25,118,210,0.08)",
                },
              }}
            >
              <AutoAwesomeIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {undoRedoButtons.map((tool) => {
          const isActive = activeTool === tool.key;
          return (
            <Tooltip key={tool.key} title={tool.label}>
              <IconButton
                size="small"
                onClick={tool.handler}
                disabled={tool.isDisabled || loading}
                sx={{
                  border: "1px solid #d0d7de",
                  borderRadius: 1.5,
                  backgroundColor: isActive ? "primary.main" : "transparent",
                  color: isActive ? "white" : "text.primary",
                  "&:hover": {
                    backgroundColor: isActive
                      ? "primary.dark"
                      : "rgba(25,118,210,0.08)",
                  },
                }}
              >
                {tool.icon}
              </IconButton>
            </Tooltip>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {numPages > 1 && (
          <Pagination
            count={numPages}
            page={pageNumber}
            onChange={handlePageChange}
            size="small"
          />
        )}

        <Tooltip title="Add Extra Form Field">
          <IconButton
            size="small"
            disabled={loading}
            onClick={onToggleAddField}
            sx={{
              border: "1px solid #d0d7de",
              borderRadius: 1.5,
              backgroundColor: addFieldOpen
                ? "rgba(25,118,210,0.12)"
                : "transparent",
            }}
          >
            <PostAddIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Object Inspector">
          <IconButton
            size="small"
            onClick={onToggleObjectInspector}
            sx={{
              border: "1px solid #d0d7de",
              borderRadius: 1.5,
              backgroundColor: objectInspectorOpen
                ? "rgba(25,118,210,0.12)"
                : "transparent",
            }}
          >
            <ObjectInspectorIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default PDFEditorControlsBar;
